/**
 * Pipeline Orchestrator API client.
 *
 * Talks to the Cloud Run pipeline orchestrator service. URL comes from
 * VITE_PIPELINE_ORCHESTRATOR_API_URL (read directly here rather than through
 * apiConfig so this module is portable across portal and admin apps without
 * forcing them to share an apiConfig shape — same pattern as PR-E1's
 * replicaClient).
 *
 * Auth: every request is signed with the user's Supabase JWT via
 * authenticatedFetch (still owned by portal at apps/portal/src/lib/api/).
 *
 * Errors: opts into authenticatedFetch's `rawResponse` flag and unwraps the
 * RFC 9457 problem+json body via pipelineErrors.unwrap. Legacy `{error}` flow
 * is unused here.
 */
import { authenticatedFetch, APIError } from '@vehix/portal/lib/api/authenticatedFetch';
import { unwrap } from './pipelineErrors';

// ============================================================================
// PIPELINE STATUS TYPES
// ============================================================================

export type PipelineStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'partial_success';

export type StepStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'skipped'
  | 'cancelled'
  | 'partial_success';

export type StepType =
  | 'data_collection'
  | 'inventory_reconciliation'
  | 'vin_dedup'
  | 'vin_processing'
  | 'post_processing'
  | 'notification';

// ============================================================================
// PIPELINE STEP
// ============================================================================

export interface PipelineStep {
  id: string;
  pipeline_run_id: string;
  parent_step_id: string | null;
  step_type: StepType;
  step_name: string;
  step_order: number;
  status: StepStatus;

  // Links to existing detail pages
  collection_run_id: string | null;
  vin_job_id: string | null;

  // Step config (for display)
  config: Record<string, unknown>;

  // Extra data (dedup summary, post-processing RPC results, etc.)
  extra?: Record<string, unknown>;

  // I/O metrics (for DAG badges)
  input_count: number | null;
  output_count: number | null;
  skipped_count: number | null;
  error_count: number | null;

  // Cost
  cost_usd: number;

  // Timing
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;

  // Error info
  error_category: string | null;
  error_message: string | null;
  retryable: boolean;

  // Retry
  attempt: number;
  max_attempts: number;
}

// ============================================================================
// PIPELINE DETAIL
// ============================================================================

export interface PipelineConfig {
  zones: Array<{ zone_slug: string; collector: string }>;
  run_mode: string;
  vin_processor_type: string;
  skip_vin_processing: boolean;
  // enable_dedup is the user-facing field. force_reprocess is the legacy alias kept for
  // backwards-compat: enable_dedup === !force_reprocess. If both are present on the wire,
  // backend lets force_reprocess win.
  enable_dedup?: boolean;
  force_reprocess?: boolean;
  notification_emails: string[];
}

export interface PipelineError {
  step_id: string;
  message: string;
  category: string;
}

export interface PipelineDetail {
  id: string;
  tenant_id: string;
  pipeline_name: string;
  run_mode: string;
  effective_run_mode: string;
  status: PipelineStatus;
  config: PipelineConfig;

  // Aggregate metrics
  total_zones: number;
  completed_zones: number;
  failed_zones: number;
  total_vins_collected: number;
  total_vins_processed: number;
  total_vins_skipped: number;
  total_vins_failed: number;
  total_vins_added: number;
  total_vins_removed: number;
  total_recalls_found: number;
  total_cost_usd: number;

  // Timing
  requested_at: string;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;

  // DAG steps (ordered for rendering)
  steps: PipelineStep[];

  // Error summary
  errors: PipelineError[];
}

// ============================================================================
// PIPELINE RUN REQUEST / RESPONSE
// ============================================================================

export interface PipelineRunRequest {
  tenant_id: string;
  zones: Array<{
    zone_slug: string;
    collector: string;
  }>;
  run_mode?: 'auto' | 'full' | 'delta';
  vin_processor_type?: 'api' | 'selenium';
  skip_vin_processing?: boolean;
  // Default false (= always reprocess every collected VIN). Send enable_dedup; force_reprocess
  // is accepted as a legacy alias and wins if both are sent.
  enable_dedup?: boolean;
  force_reprocess?: boolean;
  notification_emails?: string[];
  requested_by?: string;
}

export interface PipelineRunResponse {
  pipeline_run_id: string;
  status: 'pending';
  effective_run_mode?: string;
  total_zones: number;
  total_steps?: number;
  run_mode: string;
  message: string;
}

// ============================================================================
// PIPELINE LIST
// ============================================================================

export interface PipelineListParams {
  tenant_id?: string;
  status?: PipelineStatus;
  trigger_mode?: 'on_demand' | 'scheduled';
  limit?: number;
  offset?: number;
}

export interface PipelineListResponse {
  pipelines: PipelineDetail[];
  total: number;
}

// ============================================================================
// AVAILABLE ZONES
// ============================================================================

export interface AvailableCollector {
  collector: string;
  display_name: string;
  is_default: boolean;
}

export interface AvailableZone {
  zone_slug: string;
  zone_name: string;
  oem: string;
  state: string;
  tenant_slug?: string;
  available_collectors: AvailableCollector[];
}

// ============================================================================
// PIPELINE STEP LOG TYPES
// ============================================================================

export interface PipelineStepLogEntry {
  timestamp: string;
  level: 'info' | 'warning' | 'error' | 'success' | 'debug';
  event_type?: string;
  message: string;
  details?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

// ============================================================================
// PIPELINE SCHEDULE TYPES
// ============================================================================

export interface PipelineScheduleConfig {
  zones: Array<{ zone_slug: string; collector: string }>;
  run_mode?: 'auto' | 'full' | 'delta';
  vin_processor_type?: 'api' | 'selenium';
  skip_vin_processing?: boolean;
  enable_dedup?: boolean;
  force_reprocess?: boolean;
}

export interface PipelineSchedule {
  id: string;
  schedule_name: string;
  tenant_id: string;
  cron_expression: string;
  cron_description?: string | null;
  time_zone: string;
  pipeline_config: PipelineScheduleConfig;
  notification_emails: string[];
  notify_on_success: boolean;
  notify_on_failure: boolean;
  min_interval_minutes: number;
  description: string | null;
  is_enabled: boolean;
  last_triggered_at: string | null;
  last_run_id: string | null;
  last_run_status: PipelineStatus | null;
  created_at: string;
  updated_at: string;
  tenants?: {
    tenant_slug: string;
    tenant_name: string;
  };
}

export interface PipelineScheduleWithEtag {
  schedule: PipelineSchedule;
  etag: string | null;
}

export interface PipelineScheduleCreateRequest {
  schedule_name: string;
  tenant_id: string;
  cron_expression: string;
  time_zone?: string;
  pipeline_config: PipelineScheduleConfig;
  notification_emails?: string[];
  notify_on_success?: boolean;
  notify_on_failure?: boolean;
  min_interval_minutes?: number;
  description?: string;
}

export interface PipelineScheduleUpdateRequest {
  schedule_name?: string;
  cron_expression?: string;
  time_zone?: string;
  pipeline_config?: PipelineScheduleConfig;
  notification_emails?: string[];
  notify_on_success?: boolean;
  notify_on_failure?: boolean;
  min_interval_minutes?: number;
  description?: string;
  is_enabled?: boolean;
}

export interface PipelineScheduleListParams {
  tenant_id?: string;
  is_enabled?: boolean;
  limit?: number;
  offset?: number;
}

export interface PipelineScheduleListResponse {
  schedules: PipelineSchedule[];
  total: number;
}

// ============================================================================
// RERUN FROM STEP TYPES
// ============================================================================

export interface RerunPreviewDownstreamStep {
  id: string;
  step_type: StepType;
  step_name: string;
  step_order: number;
  status: StepStatus;
}

export interface RerunReuseSummary {
  snapshot_inventory_date: string;
  vins_in_snapshot: number;
  force_reprocess: boolean;
}

export interface RerunPreviewResponse {
  reuse_summary: RerunReuseSummary;
  downstream_steps: RerunPreviewDownstreamStep[];
}

export interface RerunFromStepRequest {
  step_id: string;
  force?: boolean;
}

export interface RerunFromStepResponse {
  pipeline_run_id: string;
  from_step_id: string;
  from_step_order: number;
  reset_step_ids: string[];
  reuse_summary: RerunReuseSummary;
  status: 'running';
}

// ============================================================================
// PIPELINE ORCHESTRATOR API CLASS
// ============================================================================

class PipelineOrchestratorAPI {
  private baseUrl: string;
  private timeout: number;

  constructor() {
    // Direct env reads (dot-access) rather than going through apiConfig so
    // this module is portable. Vite substitutes these at build time per the
    // reference_vite_env_substitution rule.
    this.baseUrl = (import.meta.env.VITE_PIPELINE_ORCHESTRATOR_API_URL as string | undefined) ?? '';
    this.timeout = parseInt(
      (import.meta.env.VITE_API_TIMEOUT as string | undefined) ?? '30000',
      10
    );
  }

  private ensureConfigured(): void {
    if (!this.baseUrl) {
      throw new APIError(
        'Pipeline Orchestrator API URL not configured',
        500,
        'Configuration Error'
      );
    }
  }

  private buildUrl(path: string, params?: Record<string, string | undefined>): string {
    const searchParams = new URLSearchParams();
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) searchParams.set(key, value);
      }
    }
    const queryString = searchParams.toString();
    return `${this.baseUrl}${path}${queryString ? `?${queryString}` : ''}`;
  }

  // ========================================================================
  // PIPELINE ENDPOINTS
  // ========================================================================

  async listPipelines(params?: PipelineListParams): Promise<PipelineListResponse> {
    this.ensureConfigured();

    const url = this.buildUrl('/pipelines', {
      tenant_id: params?.tenant_id,
      status: params?.status,
      trigger_mode: params?.trigger_mode,
      limit: params?.limit !== undefined ? String(params.limit) : undefined,
      offset: params?.offset !== undefined ? String(params.offset) : undefined
    });

    const response = await authenticatedFetch(url, {
      method: 'GET',
      timeout: this.timeout
    });

    return response.json();
  }

  async getPipeline(id: string): Promise<PipelineDetail> {
    this.ensureConfigured();

    const response = await authenticatedFetch(`${this.baseUrl}/pipelines/${id}`, {
      method: 'GET',
      timeout: this.timeout
    });

    return response.json();
  }

  async runPipeline(data: PipelineRunRequest): Promise<PipelineRunResponse> {
    this.ensureConfigured();

    const response = await authenticatedFetch(`${this.baseUrl}/pipelines/run`, {
      method: 'POST',
      body: JSON.stringify(data),
      timeout: this.timeout
    });

    return response.json();
  }

  async cancelPipeline(id: string): Promise<PipelineDetail> {
    this.ensureConfigured();

    const response = await authenticatedFetch(`${this.baseUrl}/pipelines/${id}/cancel`, {
      method: 'POST',
      timeout: this.timeout
    });

    return response.json();
  }

  async retryPipeline(id: string): Promise<PipelineDetail> {
    this.ensureConfigured();

    const response = await authenticatedFetch(`${this.baseUrl}/pipelines/${id}/retry`, {
      method: 'POST',
      timeout: this.timeout
    });

    return response.json();
  }

  // ========================================================================
  // RERUN FROM STEP ENDPOINTS
  // ========================================================================

  async getRerunPreview(runId: string, stepId: string): Promise<RerunPreviewResponse> {
    this.ensureConfigured();

    const url = this.buildUrl(`/pipelines/${runId}/rerun-preview`, { step_id: stepId });

    const response = await authenticatedFetch(url, {
      method: 'GET',
      timeout: this.timeout
    });

    return response.json();
  }

  async rerunFromStep(runId: string, body: RerunFromStepRequest): Promise<RerunFromStepResponse> {
    this.ensureConfigured();

    const response = await authenticatedFetch(
      `${this.baseUrl}/pipelines/${runId}/rerun-from-step`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        timeout: this.timeout
      }
    );

    return response.json();
  }

  // ========================================================================
  // STEP LOGS ENDPOINT
  // ========================================================================

  async getStepLogs(pipelineId: string, stepId: string): Promise<PipelineStepLogEntry[]> {
    this.ensureConfigured();

    const response = await authenticatedFetch(
      `${this.baseUrl}/pipelines/${pipelineId}/steps/${stepId}/logs`,
      { method: 'GET', timeout: this.timeout }
    );

    const data = await response.json();
    // API returns { step_id, source: "gcs", events: [...] }
    if (data && Array.isArray(data.events)) return data.events;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.logs)) return data.logs;
    return [];
  }

  // ========================================================================
  // AVAILABLE ZONES ENDPOINT
  // ========================================================================

  async getAvailableZones(tenantId: string): Promise<AvailableZone[]> {
    this.ensureConfigured();

    const url = this.buildUrl('/pipelines/available-zones', { tenant_id: tenantId });

    const response = await authenticatedFetch(url, {
      method: 'GET',
      timeout: this.timeout
    });

    const data = await response.json();
    // Normalize: API may return raw array or wrapped { zones: [...] }
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.zones)) return data.zones;
    return [];
  }

  // ========================================================================
  // SCHEDULE ENDPOINTS
  // ========================================================================

  async listSchedules(params?: PipelineScheduleListParams): Promise<PipelineScheduleListResponse> {
    this.ensureConfigured();

    const url = this.buildUrl('/schedules', {
      tenant_id: params?.tenant_id,
      is_enabled: params?.is_enabled !== undefined ? String(params.is_enabled) : undefined,
      limit: params?.limit !== undefined ? String(params.limit) : undefined,
      offset: params?.offset !== undefined ? String(params.offset) : undefined
    });

    const response = await authenticatedFetch(url, {
      method: 'GET',
      timeout: this.timeout,
      rawResponse: true
    });

    return unwrap<PipelineScheduleListResponse>(response);
  }

  async getSchedule(id: string): Promise<PipelineScheduleWithEtag> {
    this.ensureConfigured();

    const response = await authenticatedFetch(`${this.baseUrl}/schedules/${id}`, {
      method: 'GET',
      timeout: this.timeout,
      rawResponse: true
    });

    const etag = response.headers.get('ETag');
    const schedule = await unwrap<PipelineSchedule>(response);
    return { schedule, etag };
  }

  async createSchedule(
    data: PipelineScheduleCreateRequest,
    idempotencyKey?: string
  ): Promise<PipelineSchedule> {
    this.ensureConfigured();

    const headers: Record<string, string> = {};
    if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

    const response = await authenticatedFetch(`${this.baseUrl}/schedules`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers,
      timeout: this.timeout,
      rawResponse: true
    });

    return unwrap<PipelineSchedule>(response);
  }

  async updateSchedule(
    id: string,
    data: PipelineScheduleUpdateRequest,
    ifMatch?: string
  ): Promise<PipelineSchedule> {
    this.ensureConfigured();

    const payload = { ...data } as PipelineScheduleUpdateRequest & { tenant_id?: string };
    delete payload.tenant_id;

    const headers: Record<string, string> = {};
    if (ifMatch) headers['If-Match'] = ifMatch;

    const response = await authenticatedFetch(`${this.baseUrl}/schedules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
      headers,
      timeout: this.timeout,
      rawResponse: true
    });

    return unwrap<PipelineSchedule>(response);
  }

  async deleteSchedule(id: string): Promise<void> {
    this.ensureConfigured();

    const response = await authenticatedFetch(`${this.baseUrl}/schedules/${id}`, {
      method: 'DELETE',
      timeout: this.timeout,
      rawResponse: true
    });

    await unwrap<void>(response);
  }

  async triggerSchedule(id: string, idempotencyKey?: string): Promise<PipelineRunResponse> {
    this.ensureConfigured();

    const headers: Record<string, string> = {};
    if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

    const response = await authenticatedFetch(`${this.baseUrl}/schedules/${id}/trigger`, {
      method: 'POST',
      headers,
      timeout: this.timeout,
      rawResponse: true
    });

    return unwrap<PipelineRunResponse>(response);
  }
}

// Export singleton instance
export const pipelineOrchestratorAPI = new PipelineOrchestratorAPI();
